import React, {useEffect, useState} from "react";
import type { ContextProps } from '../../../../types';
import {PageTitle} from "../../../../Components/Landmarks";
import {SearchResultsContainer} from "../../../../Components/SearchResults/SearchResultsContainer";
import {Button, inputStyles, SelectField, TextField} from "../../../../Components/Inputs";
import {GridCell, gridConstants, GridContainer} from "../../../../Components/Grid";
import {useInput} from "../../../../Components/Inputs/inputUtility";
import {LinkButton, ExternalLink} from "../../../../Components/Link";
import {locations} from "../../../../Utilities/Location";
import {allow} from "../../../../Components/Authorize";
import {isArrayNullOrEmpty} from "../../../../Utilities/Types/arrayUtilities";
import {isObjectNullOrEmpty} from "../../../../Utilities/Types/objectUtilities";
import {ownCatalogApi} from "./ownCatalogApi";
import {OwnSearchResultsView} from "./OwnSearchResultsView";
import {emptyGuid} from "../../../../Utilities/commonUtilities";
import * as PolicyEvents from "../../../../Components/Authorize/policyEventsConstants";
import {ownApi} from "../ownApi";

import { isRmtcAdmin } from "@/Utilities/LocalStorage/storageUtilities";
import {LOAN_LIBRARY_MEDIA_FORMAT_LIST} from "../../LoanLibrary/loanLibraryConstants";
import {SYSTEM_TYPES} from "../../libraryConstants";



export const OwnCatalogContainer = ({
                                        ownSearchCriteria,
                                        handleApiCall,
                                        handleSetOwnSearchCriteria,
                                        redirect,
                                        systemType,                                        
                                    }: ContextProps) => {

    const [ownResources, setOwnResources] = useState<Record<string, unknown>[]>([]);
    const [ownTotalRecords, setOwnTotalRecords] = useState(0);


    const keywordSearch = useInput("");
    const barCodeSearch = useInput("");
    const objectTypeSearch = useInput("");

    const canManageOwnCatalogItems = allow(PolicyEvents.MANAGE_OWN_CATALOG_ITEMS);

    const loadOwnResourcesResult = (result: { ownCatalogItems: Record<string, unknown>[]; totalRecords: number }) => {
        setOwnResources(result.ownCatalogItems);
        setOwnTotalRecords(result.totalRecords);
    };

    const getOwnResources = () => {
        if (isObjectNullOrEmpty(ownSearchCriteria)) return;

        const updatedCriteria = {...ownSearchCriteria, searchTerm: keywordSearch.value, loadSearchResults: true}
        handleApiCall(() => ownCatalogApi.getOwnCatalogResources(updatedCriteria), (result: { ownCatalogItems: Record<string, unknown>[]; totalRecords: number }) => loadOwnResourcesResult(result));

    };

    const handleEdit = (ownCatalogId: string) => {
        redirect(locations.OWN_CATALOG_ITEM.getLink([ownCatalogId, systemType]).to);
    }

    const handleAddToQueue = async (ownCatalogId: string) => {
        handleApiCall(() => ownApi.addOwnQueueItem(ownCatalogId));
    }


    const handleSearch = () => {
        if (isObjectNullOrEmpty(ownSearchCriteria)) return;

        const updatedCriteria = {
            ...ownSearchCriteria,
            searchTerm: keywordSearch.value,
            catalogNumber: barCodeSearch.value,
            objectType: objectTypeSearch.value || null,
        };

        handleApiCall(
            () => ownCatalogApi.getOwnCatalogResources(updatedCriteria), (result: { ownCatalogItems: Record<string, unknown>[]; totalRecords: number }) => loadOwnResourcesResult(result)
        );
    };

    const noResults = isArrayNullOrEmpty(ownResources);

    useEffect(() => {
        if (!ownSearchCriteria) return;

        if (ownSearchCriteria.loadSearchResults)
            getOwnResources();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ownSearchCriteria, systemType, keywordSearch.value]);

    useEffect(() => {
        if (!isObjectNullOrEmpty(ownSearchCriteria)) {
            getOwnResources();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ownSearchCriteria, keywordSearch.value]);

    if (!ownSearchCriteria) return null;

    return (
        <>
            <PageTitle h1={`Own Library Catalog`}/>

         

            <div className="searchBox margin--bottom">
                <div className="searchBox__title">Search the catalog</div>
                <GridContainer isReverseOrder hasMarginX hasMarginY className="searchBox__input">

                    <TextField
                        label={`keyword Search box`}
                        labelClass={`show-for-sr`}
                        large_columns={gridConstants.column.TWELVE}
                        medium_columns={gridConstants.column.EIGHT}
                        name={`txtSearch`}
                        placeholder={`Product Name, Description, or Keywords`}
                        {...keywordSearch}
                    />
                    
                    <TextField
                        label="barCode Search box"
                        labelClass="show-for-sr"
                        large_columns={gridConstants.column.EIGHT}
                        medium_columns={gridConstants.column.EIGHT}
                        name="barCodeSearch"
                        placeholder="Catalog Number"
                        {...barCodeSearch}
                    />
                
                    <SelectField
                        label="Object Type"
                        name="objectTypeSearch"
                        options={LOAN_LIBRARY_MEDIA_FORMAT_LIST}
                        large_columns={gridConstants.column.FOUR}
                        includeDefaultOption
                        defaultOption="All Object Types"
                        hideLabel
                        {...objectTypeSearch}
                    />
                                     
                    <GridCell  large_columns={gridConstants.column.TWO} medium_columns={gridConstants.column.FOUR}>
                        <Button
                            buttonType={inputStyles.button.types.SEARCH}
                            label="Search"
                            name="btnSearch"
                            onClick={handleSearch}
                            isPrimary

                        />
                    </GridCell>
                </GridContainer>
            </div>

            <GridContainer>
                <GridCell className="text-center">
                    <LinkButton {...locations.OWN_WHATS_NEW.getLink()} />
                    <LinkButton {...locations.OWN_QUEUE.getLink()} />
                    <LinkButton {...locations.OWN_LOAN_HISTORY.getLink()} />
                    {
                        canManageOwnCatalogItems && (
                            <LinkButton
                                {...locations.OWN_CATALOG_ITEM.getLink([emptyGuid, systemType])}
                                label="Add New Item"
                            />
                        )
                    }
                    {
                        isRmtcAdmin() && (
                            <>
                                <LinkButton {...locations.SHIPPING_LOCATION_ADD.link} label={"Manage Shipping Locations"} />
                                <LinkButton {...locations.LIBRARY_VENDOR.getLink([emptyGuid, SYSTEM_TYPES.Own])} />
                                <LinkButton {...locations.LOAN_LIBRARY_MANUFACTURER.getLink([emptyGuid])} />
                            </>
                        )
                    }
                </GridCell>
            </GridContainer>

            <SearchResultsContainer className="catalog">
                {ownResources && !noResults && (
                    <OwnSearchResultsView
                        ownResources={ownResources}
                        ownSearchCriteria={ownSearchCriteria}
                        canManageOwnCatalogItems={canManageOwnCatalogItems}
                        handleAddToQueue={handleAddToQueue}
                        handleEdit={handleEdit}
                        handleSetOwnSearchCriteria={handleSetOwnSearchCriteria}
                        redirect={redirect}
                        totalRecords={ownTotalRecords}
                    />
                )}
                {noResults && (
                    <p className="text-center">
                        No results.
                    </p>
                )}
            </SearchResultsContainer>
        </>
    );
};
